// import { useCallback } from 'react';
import { useMemo } from 'react';
import _ from 'lodash';

import { IHealthPlanDeductibleTriggerConfigDto } from '@/dtos/models/IHealthPlanDeductibleTriggerConfigDto';
import { CategorySpendLimitConfig, HealthPlanDeductibleConfig } from '@/modules/HealthPlan/HealthPlan.types';

import {
  CategorySpendLimitPayload,
  CoverageLevelPayload,
  DEDUCTIBLE_FIELDS,
  EMBEDDED_AMOUNT_FIELD,
  MEMBER_DEDUCTIBLE_FIELD,
  PLAN_DEDUCTIBLE_FIELD,
} from '../CoverageLevels.constants';
import { mapTriggerConfigsToAmounts } from '../CoverageLevels.utils';
import { useCoverageLevelsQuery } from '../queries';
const getAmountFromTriggerConfigs = (
  triggerConfigs: IHealthPlanDeductibleTriggerConfigDto[] | undefined,
  triggerId: number
): string => {
  const config = triggerConfigs?.find((tc) => tc.trigger_id === triggerId);
  return _.toString(config?.threshold_amount ?? '');
};

const useGetFormattedCoverageLevels = (
  values?: HealthPlanDeductibleConfig[],
  categorySpendLimits?: CategorySpendLimitConfig[]
) => {
  const { data: coverageLevels } = useCoverageLevelsQuery();

  const formatedData: CoverageLevelPayload[] = useMemo(() => {
    if (values) {
      const newValues = values.map((value) => {
        const currentItem = coverageLevels?.find((item) => item.id === `${value.coverage?.id}`);
        const coverageId = `${value.coverage?.id}`;

        // Map category_spend_limits for this coverage level
        const mappedCategoryLimits: CategorySpendLimitPayload[] = (categorySpendLimits || [])
          .filter(
            (limit) =>
              // Include limits that match this coverage_id, or HP-level limits (no coverage_id)
              limit.coverage_id == null || `${limit.coverage_id}` === coverageId
          )
          .map((limit) => ({
            id: _.toString(limit.id ?? ''),
            planId: _.toString(limit.plan_id ?? ''),
            categoryType: limit.category_type,
            categorySpendLimit: _.toString(limit.category_spend_limit ?? ''),
          }));

        // Read deductible amounts from trigger_configs
        const triggerConfigs = value.trigger_configs;
        const planDeductibleTrigger = DEDUCTIBLE_FIELDS.find((t) => t.name === PLAN_DEDUCTIBLE_FIELD);
        const memberDeductibleTrigger = DEDUCTIBLE_FIELDS.find((t) => t.name === MEMBER_DEDUCTIBLE_FIELD);
        const embeddedAmountTrigger = DEDUCTIBLE_FIELDS.find((t) => t.name === EMBEDDED_AMOUNT_FIELD);

        const customTriggerAmounts = mapTriggerConfigsToAmounts(triggerConfigs);

        return {
          id: `${value.id}`,
          name: `${value.coverage?.name}`,
          coverageId,
          parentId: `${value.parent_id}`,
          title: currentItem?.name || '',
          coverageCode: `${value?.code?.code || ''}`,
          planDeductible: planDeductibleTrigger
            ? getAmountFromTriggerConfigs(triggerConfigs, planDeductibleTrigger.triggerId)
            : '',
          memberDeductible: memberDeductibleTrigger
            ? getAmountFromTriggerConfigs(triggerConfigs, memberDeductibleTrigger.triggerId)
            : '',
          embeddedAmount: embeddedAmountTrigger
            ? getAmountFromTriggerConfigs(triggerConfigs, embeddedAmountTrigger.triggerId)
            : '',

          isDeletable: !value.coverage?.is_default,
          accountFundingConfigs: value.auto_enroll_account_funding_configs?.map((item) => ({
            id: `${item.id || ''}`,
            planId: `${item.plan_id || ''}`,
            planName: item.plan_name,
            autoEnrollmentAmount: `${item.auto_enrollment_amount ?? ''}`,
            perPersonSpendLimit: `${item.per_person_spend_limit_amount ?? ''}`,
          })),
          categorySpendLimits: mappedCategoryLimits,
          customTriggerAmounts,
        };
      });

      return newValues;
    }
    return [];
  }, [coverageLevels, values, categorySpendLimits]);
  return formatedData;
};

export default useGetFormattedCoverageLevels;
